import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState
} from 'react'
import styled from '@emotion/styled'
import {AnimatePresence, motion} from 'motion/react'
import {v4 as uuid} from 'uuid'

import {Portal} from '../components/designSystem/helpers/Portal'
import {Props as ToastProps, Toast} from '../components/designSystem/Toast'
import {cssTheme} from '../themes'

type ToastType = {
  id: string
  content: ReactNode
  toastProps?: Omit<ToastProps, 'children'>
  bottomSpacing?: string
}

/**
 * Function type for opening a toast notification
 *
 * @param content - The content to display in the toast (ReactNode)
 * @param toastProps - Optional props for the toast (icon, autoClose, singleToastMessage, etc.)
 * @param bottomSpacing - Optional custom spacing from the bottom of the viewport (e.g., cssTheme.layout.spacing.s700).
 *                        Useful for avoiding overlaps with floating UI elements. Defaults to s300.
 * @returns The unique ID of the created toast
 */
export type ToastOpenType = (
  content: ReactNode,
  toastProps?: Omit<ToastProps, 'children'> & {
    singleToastMessage?: boolean
  },
  bottomSpacing?: string
) => string

export interface ToastContextValue {
  open: ToastOpenType
  close: (id: string) => void
  closeAll: () => void
}

const ToastWrapper = styled(motion.div, {
  shouldForwardProp: propName => propName !== 'bottomSpacing'
})<{bottomSpacing?: string}>`
  position: fixed;
  z-index: 110;
  /*needed to apply extra padding at the bottom for iOS gesture bar*/
  --safe-area-inset-bottom: env(safe-area-inset-bottom);
  bottom: calc(${props => props.bottomSpacing} + var(--safe-area-inset-bottom));
  right: ${cssTheme.layout.spacing.s300};
  left: ${cssTheme.layout.spacing.s300};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s300};
  pointer-events: none;
`

export const ToastContext = createContext<ToastContextValue>({
  open: () => '',
  close: () => null,
  closeAll: () => null
})

/**
 * Provider component that manages toast notifications
 * @param children - Child components that will have access to toast functionality
 * @param bottomSpacing - Optional default bottom spacing for all toasts in this provider context
 *                        Can be overridden per-toast using the third parameter of toast.open()
 */
export const ToastProvider = ({
  children,
  bottomSpacing = cssTheme.layout.spacing.s300
}: {
  children: ReactNode
  bottomSpacing?: string
}) => {
  const [toasts, setToasts] = useState<ToastType[]>([])

  const open: ToastOpenType = (content, toastProps, bottomSpacing): string => {
    const id = uuid()
    const toast = {id, content, toastProps, bottomSpacing}
    if (toastProps?.singleToastMessage) {
      setToasts([toast])
    } else {
      setToasts(currentToasts => [...currentToasts, toast])
    }
    return id
  }

  const close: (id: string) => void = id =>
    setToasts(currentToasts => currentToasts.filter(toast => toast.id !== id))

  const closeAll: () => void = () => setToasts([])

  const contextValue = useMemo(() => ({open, close, closeAll}), [])

  // Use bottomSpacing from first toast, fall back to provider prop
  const activeBottomSpacing = toasts[0]?.bottomSpacing || bottomSpacing

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      <Portal>
        <ToastWrapper bottomSpacing={activeBottomSpacing}>
          <AnimatePresence>
            {toasts.map(toast => {
              const {toastProps} = toast
              return (
                <Toast
                  key={toast.id}
                  {...toastProps}
                  onClose={() => {
                    close(toast.id)
                    toastProps?.onClose && toastProps.onClose()
                  }}
                >
                  {toast.content}
                </Toast>
              )
            })}
          </AnimatePresence>
        </ToastWrapper>
      </Portal>
    </ToastContext.Provider>
  )
}

export const useToastController = () => {
  const context = useContext(ToastContext)

  // Ensure context is not undefined
  if (!context) {
    throw new Error('useToastController must be used within a ToastProvider')
  }

  const {open, close, closeAll} = context

  return {
    open,
    close: (toastId?: string) => {
      if (toastId) {
        close(toastId)
      } else {
        closeAll()
      }
    },
    closeAll
  }
}
